import { Controller } from "@hotwired/stimulus"

const POLL_INTERVAL_MS = 3000

// Watches lightweight server-owned workspace state. Connect screens poll while
// visible so authorization and the first agent-saved strategy arrive without a
// reload. The strategy board checks only when it is loaded or brought back into
// focus, avoiding a permanent poll on an otherwise read-only page.
export default class extends Controller {
  static values = {
    statusUrl: String,
    connectedAgentKeys: Array,
    strategyRevision: String,
    strategyCount: Number,
    strategyCheckMode: { type: String, default: "none" }
  }

  connect() {
    this.initialKeys = this.hasConnectedAgentKeysValue ? this.#normalize(this.connectedAgentKeysValue) : null
    this.initialStrategyRevision = this.hasStrategyRevisionValue ? this.strategyRevisionValue : null
    this.initialStrategyCount = this.hasStrategyCountValue ? this.strategyCountValue : null
    this.timer = null
    this.inFlight = false
    this.checkPending = false
    this.stopped = false
    this.abortController = null
    this.visibilityHandler = () => this.#visibilityChanged()
    this.focusHandler = () => this.#schedule(0)

    document.addEventListener("visibilitychange", this.visibilityHandler)
    window.addEventListener("focus", this.focusHandler)

    const delay = this.#tracksStrategies() ? 0 : POLL_INTERVAL_MS
    this.#schedule(delay)
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.visibilityHandler)
    window.removeEventListener("focus", this.focusHandler)
    this.#stop()
  }

  #schedule(delay) {
    window.clearTimeout(this.timer)
    if (this.stopped || document.hidden || !this.hasStatusUrlValue) return

    this.timer = window.setTimeout(() => this.#check(), delay)
  }

  async #check() {
    if (this.stopped || document.hidden) return
    if (this.inFlight) {
      this.checkPending = true
      return
    }

    this.inFlight = true
    this.abortController = new AbortController()

    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin",
        cache: "no-store",
        signal: this.abortController.signal
      })
      if (!response.ok) return

      const payload = await response.json()
      if (this.#strategyChanged(payload)) {
        this.#visit(this.#strategyDestination(payload))
        return
      }

      if (this.#connectionsChanged(payload)) this.#visit(window.location.href)
    } catch (error) {
      if (error.name !== "AbortError") {
        // Connect screens retry. Boards retain their content and check again
        // the next time the page becomes active.
      }
    } finally {
      this.inFlight = false
      this.abortController = null
      if (this.checkPending) {
        this.checkPending = false
        this.#schedule(0)
      } else if (this.#pollsContinuously()) {
        this.#schedule(POLL_INTERVAL_MS)
      }
    }
  }

  #strategyChanged(payload) {
    return this.#tracksStrategies() &&
      typeof payload.strategy_revision === "string" &&
      payload.strategy_revision !== this.initialStrategyRevision
  }

  #connectionsChanged(payload) {
    if (this.initialKeys === null || !Array.isArray(payload.connected_agent_keys)) return false

    return !this.#sameKeys(this.#normalize(payload.connected_agent_keys), this.initialKeys)
  }

  #strategyDestination(payload) {
    const firstStrategyAppeared = this.initialStrategyCount === 0 &&
      Number.isInteger(payload.strategy_count) &&
      payload.strategy_count > 0

    if (firstStrategyAppeared && typeof payload.strategies_home_path === "string") {
      return payload.strategies_home_path
    }

    return window.location.href
  }

  #visit(url) {
    this.#stop()
    const Turbo = window.Turbo || globalThis.Turbo
    if (Turbo?.visit) {
      Turbo.visit(url, { action: "replace" })
    } else {
      window.location.replace(url)
    }
  }

  #visibilityChanged() {
    if (document.hidden) {
      window.clearTimeout(this.timer)
      this.abortController?.abort()
      return
    }

    this.#schedule(0)
  }

  #tracksStrategies() {
    return this.initialStrategyRevision !== null && this.strategyCheckModeValue !== "none"
  }

  #pollsContinuously() {
    return this.initialKeys !== null || this.strategyCheckModeValue === "poll"
  }

  #stop() {
    this.stopped = true
    window.clearTimeout(this.timer)
    this.abortController?.abort()
  }

  #normalize(keys) {
    return [...new Set(keys.map((key) => String(key)))].sort()
  }

  #sameKeys(left, right) {
    return left.length === right.length && left.every((key, index) => key === right[index])
  }
}
